import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
	Box,
	Card,
	CardContent,
	Typography,
	Stack,
	Button,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	TextField,
	IconButton,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	Grid,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Alert,
	Snackbar,
	RadioGroup,
	Radio,
	FormControlLabel,
	Divider,
	CircularProgress,
} from "@mui/material";
import { X, Edit2, Plus, Repeat, AlertCircle } from "lucide-react";
import { supabase } from "../../config/supabase";
import { useAuth } from "../../contexts/AuthContext";
import { useAcademicYear } from "../../contexts/AcademicYearContext";
import {
	getAcademicYearRange,
	scheduleOverlapsAcademicYear,
} from "../../utils/academicYear";

// Removed Sunday from the days of the week
const DAYS_OF_WEEK = [
	{ value: 1, label: "Monday" },
	{ value: 2, label: "Tuesday" },
	{ value: 3, label: "Wednesday" },
	{ value: 4, label: "Thursday" },
	{ value: 5, label: "Friday" },
	{ value: 6, label: "Saturday" },
];

const PERIODS = Array.from({ length: 10 }, (_, i) => ({
	value: i + 1,
	label: `Period ${i + 1}`,
}));

const CourseSchedule = () => {
	const { user } = useAuth();
	const { currentAcademicYear, loading: academicYearLoading } =
		useAcademicYear();

	// All timetable rows for this teacher (every assigned grade)
	const [allSchedules, setAllSchedules] = useState([]);
	const [loading, setLoading] = useState(true);
	const [teacherGradeOptions, setTeacherGradeOptions] = useState([]);
	const [formGradeId, setFormGradeId] = useState("");
	const [weeklyGradeId, setWeeklyGradeId] = useState(null);
	const [overviewGradeId, setOverviewGradeId] = useState(null);

	// State for schedule form
	const [selectedDay, setSelectedDay] = useState("");
	const [selectedPeriod, setSelectedPeriod] = useState("");
	const [startDate, setStartDate] = useState("");
	const [endDate, setEndDate] = useState("");
	const [scheduleType, setScheduleType] = useState("recurring");
	const [recurringPattern, setRecurringPattern] = useState({
		frequency: "weekly",
		repeatEvery: 1,
		endsOn: "never",
		occurrences: 12,
	});

	// Dialog states
	const [addDialog, setAddDialog] = useState(false);
	const [editDialog, setEditDialog] = useState(false);
	const [selectedSchedule, setSelectedSchedule] = useState(null);
	const [isSubmitting, setIsSubmitting] = useState(false);

	// Alert states
	const [alert, setAlert] = useState({
		open: false,
		message: "",
		severity: "success",
	});

	// Default grade selectors to the teacher's assigned classes (not global header grade).
	useEffect(() => {
		if (!teacherGradeOptions.length) {
			setWeeklyGradeId(null);
			setOverviewGradeId(null);
			return;
		}

		const pickDefault = (prev) => {
			if (prev && teacherGradeOptions.some((g) => g.id === prev)) return prev;
			return teacherGradeOptions[0].id;
		};

		setWeeklyGradeId(pickDefault);
		setOverviewGradeId(pickDefault);
	}, [teacherGradeOptions]);
	useEffect(() => {
		if (!user?.id) {
			setTeacherGradeOptions([]);
			return;
		}
		const loadGrades = async () => {
			try {
				const { data, error } = await supabase
					.from("teacher_grades")
					.select(
						`
						grade_id,
						grades:grade_id ( id, name )
					`
					)
					.eq("profile_id", user.id);

				if (error) throw error;
				const opts = (data || [])
					.map((row) => ({
						id: row.grade_id,
						name: row.grades?.name || "Grade",
					}))
					.filter((o) => o.id);
				opts.sort((a, b) =>
					String(a.name).localeCompare(String(b.name), undefined, {
						numeric: true,
					})
				);
				setTeacherGradeOptions(opts);
			} catch (e) {
				console.error("Error fetching teacher grades:", e);
				setTeacherGradeOptions([]);
			}
		};
		loadGrades();
	}, [user?.id]);

	const fetchAllSchedules = useCallback(async () => {
		if (!user?.id || !currentAcademicYear) return;

		try {
			setLoading(true);
			const { start, end } = getAcademicYearRange(currentAcademicYear);
			const ayStart = start.format("YYYY-MM-DD");
			const ayEnd = end.format("YYYY-MM-DD");

			const { data, error } = await supabase
				.from("teacher_schedules")
				.select("*")
				.eq("teacher_id", user.id)
				.lte("start_date", ayEnd)
				.or(`end_date.is.null,end_date.gte.${ayStart}`)
				.order("grade_id")
				.order("day_of_week")
				.order("period");

			if (error) throw error;

			const yearSchedules = (data || []).filter((schedule) =>
				scheduleOverlapsAcademicYear(schedule, currentAcademicYear)
			);
			setAllSchedules(yearSchedules);
		} catch (error) {
			console.error("Error fetching schedules:", error);
			setAlert({
				open: true,
				message: "Error fetching timetable",
				severity: "error",
			});
		} finally {
			setLoading(false);
		}
	}, [user?.id, currentAcademicYear]);

	useEffect(() => {
		if (!user?.id) {
			setAllSchedules([]);
			setLoading(false);
			return;
		}
		if (academicYearLoading) return;
		fetchAllSchedules();
	}, [user?.id, academicYearLoading, fetchAllSchedules]);

	const schedulesForTable = useMemo(
		() =>
			weeklyGradeId
				? allSchedules.filter((s) => s.grade_id === weeklyGradeId)
				: [],
		[allSchedules, weeklyGradeId]
	);

	const schedulesForOverview = useMemo(
		() =>
			overviewGradeId
				? allSchedules.filter((s) => s.grade_id === overviewGradeId)
				: [],
		[allSchedules, overviewGradeId]
	);

	const weeklyGradeName = useMemo(
		() => teacherGradeOptions.find((g) => g.id === weeklyGradeId)?.name,
		[teacherGradeOptions, weeklyGradeId]
	);

	const overviewGradeName = useMemo(
		() => teacherGradeOptions.find((g) => g.id === overviewGradeId)?.name,
		[teacherGradeOptions, overviewGradeId]
	);

	const handleAddSchedule = async () => {
		if (!formGradeId) {
			showAlert("Please select a grade for this timetable entry", "error");
			return;
		}

		try {
			// Validate required fields
			if (!selectedDay || !selectedPeriod || !startDate) {
				showAlert("Please fill in all required fields", "error");
				return;
			}

			setIsSubmitting(true);

			// Check if schedule already exists for this day and period
			// const { data: existingSchedules, error: checkError } = await supabase
			// 	.from("teacher_schedules")
			// 	.select("*")
			// 	.eq("teacher_id", user.id)
			// 	.eq("grade_id", selectedGrade)
			// 	.eq("day_of_week", selectedDay)
			// 	.eq("period", selectedPeriod)
			// 	.eq("status", "active");

			// if (checkError) throw checkError;

			// if (existingSchedules && existingSchedules.length > 0) {
			// 	showAlert(
			// 		`You already have a schedule for ${getDayLabel(
			// 			selectedDay
			// 		)} period ${selectedPeriod}. Please choose a different day or period.`,
			// 		"error"
			// 	);
			// 	setIsSubmitting(false);
			// 	return;
			// }

			// Format dates properly
			const formattedStartDate = new Date(startDate)
				.toISOString()
				.split("T")[0];
			const formattedEndDate = endDate
				? new Date(endDate).toISOString().split("T")[0]
				: null;

			// Validate recurring pattern if selected
			if (scheduleType === "recurring") {
				if (!recurringPattern.frequency || !recurringPattern.repeatEvery) {
					showAlert("Please fill in all recurring timetable details", "error");
					setIsSubmitting(false);
					return;
				}
			}

			const scheduleData = {
				teacher_id: user.id,
				grade_id: formGradeId,
				day_of_week: selectedDay,
				period: selectedPeriod,
				start_date: formattedStartDate,
				end_date: scheduleType === "single" ? formattedEndDate : null,
				status: "active",
				schedule_type: scheduleType,
				recurring_pattern:
					scheduleType === "recurring"
						? {
								frequency: recurringPattern.frequency,
								repeatEvery: Number(recurringPattern.repeatEvery),
								endsOn: recurringPattern.endsOn,
								occurrences:
									recurringPattern.endsOn === "after"
										? Number(recurringPattern.occurrences)
										: null,
						  }
						: null,
			};

			const { data, error } = await supabase
				.from("teacher_schedules")
				.insert([scheduleData])
				.select()
				.single();

			if (error) throw error;

			await fetchAllSchedules();
			setAddDialog(false);
			resetForm();
			showAlert("Timetable entry added successfully", "success");
		} catch (error) {
			console.error("Error adding schedule:", error);
			showAlert(error.message || "Error adding timetable entry", "error");
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleUpdateSchedule = async () => {
		if (!selectedSchedule) return;

		try {
			if (!selectedDay || !selectedPeriod || !startDate) {
				showAlert("Please fill in all required fields", "error");
				return;
			}

			if (!formGradeId) {
				showAlert("Please select a grade for this timetable entry", "error");
				return;
			}

			setIsSubmitting(true);

			// Check if schedule already exists for this day, period, and grade (excluding current schedule)
			const { data: existingSchedules, error: checkError } = await supabase
				.from("teacher_schedules")
				.select("*")
				.eq("teacher_id", user.id)
				.eq("grade_id", formGradeId)
				.eq("day_of_week", selectedDay)
				.eq("period", selectedPeriod)
				.eq("status", "active")
				.neq("id", selectedSchedule.id); // Exclude current schedule from check

			if (checkError) throw checkError;

			if (existingSchedules && existingSchedules.length > 0) {
				showAlert(
					`You already have a timetable entry for ${getDayLabel(
						selectedDay
					)} period ${selectedPeriod} for this grade. Please choose a different day or period.`,
					"error"
				);
				setIsSubmitting(false);
				return;
			}

			const formattedStartDate = new Date(startDate)
				.toISOString()
				.split("T")[0];
			const formattedEndDate = endDate
				? new Date(endDate).toISOString().split("T")[0]
				: null;

			const scheduleData = {
				grade_id: formGradeId,
				day_of_week: selectedDay,
				period: selectedPeriod,
				start_date: formattedStartDate,
				end_date: scheduleType === "single" ? formattedEndDate : null,
				schedule_type: scheduleType,
				recurring_pattern:
					scheduleType === "recurring"
						? {
								frequency: recurringPattern.frequency,
								repeatEvery: Number(recurringPattern.repeatEvery),
								endsOn: recurringPattern.endsOn,
								occurrences:
									recurringPattern.endsOn === "after"
										? Number(recurringPattern.occurrences)
										: null,
						  }
						: null,
			};

			const { error } = await supabase
				.from("teacher_schedules")
				.update(scheduleData)
				.eq("id", selectedSchedule.id);

			if (error) throw error;

			await fetchAllSchedules();
			setEditDialog(false);
			resetForm();
			showAlert("Timetable entry updated successfully", "success");
		} catch (error) {
			console.error("Error updating schedule:", error);
			showAlert(error.message || "Error updating timetable entry", "error");
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleCancelSchedule = async (scheduleId) => {
		try {
			const { error } = await supabase
				.from("teacher_schedules")
				.update({ status: "cancelled" })
				.eq("id", scheduleId);

			if (error) throw error;
			await fetchAllSchedules();
			showAlert("Timetable entry cancelled successfully", "success");
		} catch (error) {
			console.error("Error cancelling schedule:", error);
			showAlert("Error cancelling timetable entry", "error");
		}
	};

	const openEditDialog = (schedule) => {
		setSelectedSchedule(schedule);
		setFormGradeId(schedule.grade_id || "");
		setSelectedDay(schedule.day_of_week);
		setSelectedPeriod(schedule.period);
		setStartDate(schedule.start_date);
		setEndDate(schedule.end_date || "");
		setScheduleType(schedule.schedule_type || "single");
		setRecurringPattern(
			schedule.recurring_pattern || {
				frequency: "weekly",
				repeatEvery: 1,
				endsOn: "never",
				occurrences: 12,
			}
		);
		setEditDialog(true);
	};

	const resetForm = () => {
		setSelectedDay("");
		setSelectedPeriod("");
		setStartDate("");
		setEndDate("");
		setSelectedSchedule(null);
		setScheduleType("recurring");
		setRecurringPattern({
			frequency: "weekly",
			repeatEvery: 1,
			endsOn: "never",
			occurrences: 12,
		});
	};

	const showAlert = (message, severity = "success") => {
		setAlert({
			open: true,
			message,
			severity,
		});
	};

	const ScheduleFormContent = () => (
		<Stack spacing={2}>
			<FormControl fullWidth>
				<InputLabel>Grade</InputLabel>
				<Select
					value={formGradeId || ""}
					label="Grade"
					onChange={(e) => setFormGradeId(e.target.value)}
					disabled={isSubmitting || teacherGradeOptions.length === 0}>
					{teacherGradeOptions.map((g) => (
						<MenuItem key={g.id} value={g.id}>
							{g.name}
						</MenuItem>
					))}
				</Select>
			</FormControl>

			<FormControl fullWidth>
				<InputLabel>Day of Week</InputLabel>
				<Select
					value={selectedDay}
					label="Day of Week"
					onChange={(e) => setSelectedDay(e.target.value)}
					disabled={isSubmitting}>
					{DAYS_OF_WEEK.map((day) => (
						<MenuItem key={day.value} value={day.value}>
							{day.label}
						</MenuItem>
					))}
				</Select>
			</FormControl>

			<FormControl fullWidth>
				<InputLabel>Period</InputLabel>
				<Select
					value={selectedPeriod}
					label="Period"
					onChange={(e) => setSelectedPeriod(e.target.value)}
					disabled={isSubmitting}>
					{PERIODS.map((period) => (
						<MenuItem key={period.value} value={period.value}>
							{period.label}
						</MenuItem>
					))}
				</Select>
			</FormControl>

			<TextField
				label="Start Date"
				type="date"
				value={startDate}
				onChange={(e) => setStartDate(e.target.value)}
				InputLabelProps={{ shrink: true }}
				fullWidth
				disabled={isSubmitting}
			/>

			<FormControl>
				<RadioGroup
					row
					value={scheduleType}
					onChange={(e) => setScheduleType(e.target.value)}
					disabled={isSubmitting}>
					<FormControlLabel
						value="single"
						control={<Radio size="small" disabled={isSubmitting} />}
						label="Single"
					/>
					<FormControlLabel
						value="recurring"
						control={<Radio size="small" disabled={isSubmitting} />}
						label="Recurring"
					/>
				</RadioGroup>
			</FormControl>

			{scheduleType === "single" ? (
				<TextField
					label="End Date (Optional)"
					type="date"
					value={endDate}
					onChange={(e) => setEndDate(e.target.value)}
					InputLabelProps={{ shrink: true }}
					fullWidth
					disabled={isSubmitting}
				/>
			) : (
				<Stack spacing={2}>
					<Grid container spacing={2}>
						<Grid item xs={6}>
							<FormControl fullWidth size="small">
								<InputLabel>Frequency</InputLabel>
								<Select
									value={recurringPattern.frequency}
									label="Frequency"
									onChange={(e) =>
										setRecurringPattern((prev) => ({
											...prev,
											frequency: e.target.value,
										}))
									}
									disabled={isSubmitting}>
									<MenuItem value="weekly">Weekly</MenuItem>
									<MenuItem value="monthly">Monthly</MenuItem>
								</Select>
							</FormControl>
						</Grid>
						<Grid item xs={6}>
							<FormControl fullWidth size="small">
								<InputLabel>Repeat Every</InputLabel>
								<Select
									value={recurringPattern.repeatEvery}
									label="Repeat Every"
									onChange={(e) =>
										setRecurringPattern((prev) => ({
											...prev,
											repeatEvery: e.target.value,
										}))
									}
									disabled={isSubmitting}>
									{[1, 2, 3, 4].map((num) => (
										<MenuItem key={num} value={num}>
											{num}{" "}
											{recurringPattern.frequency === "weekly"
												? "Week(s)"
												: "Month(s)"}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						</Grid>
					</Grid>

					<Grid container spacing={2}>
						<Grid item xs={6}>
							<FormControl fullWidth size="small">
								<InputLabel>Ends</InputLabel>
								<Select
									value={recurringPattern.endsOn}
									label="Ends"
									onChange={(e) =>
										setRecurringPattern((prev) => ({
											...prev,
											endsOn: e.target.value,
										}))
									}
									disabled={isSubmitting}>
									<MenuItem value="never">Never</MenuItem>
									<MenuItem value="after">After Occurrences</MenuItem>
								</Select>
							</FormControl>
						</Grid>
						{recurringPattern.endsOn === "after" && (
							<Grid item xs={6}>
								<TextField
									label="Occurrences"
									type="number"
									size="small"
									value={recurringPattern.occurrences}
									onChange={(e) =>
										setRecurringPattern((prev) => ({
											...prev,
											occurrences: parseInt(e.target.value),
										}))
									}
									InputProps={{ inputProps: { min: 1 } }}
									fullWidth
									disabled={isSubmitting}
								/>
							</Grid>
						)}
					</Grid>
				</Stack>
			)}

			{/* Display selected grade in the form */}
			{formGradeId && (
				<Box sx={{ mt: 2 }}>
					<Typography variant="body2" color="text.secondary">
						This entry applies to grade:{" "}
						<strong>
							{teacherGradeOptions.find((g) => g.id === formGradeId)?.name ||
								"—"}
						</strong>
					</Typography>
				</Box>
			)}
		</Stack>
	);

	// Helper function to get day label from day value
	const getDayLabel = (dayValue) => {
		const day = DAYS_OF_WEEK.find((d) => d.value === dayValue);
		return day ? day.label : "Unknown";
	};

	return (
		<Box sx={{ p: 2 }}>
			{teacherGradeOptions.length === 0 && !loading && (
				<Alert severity="info" icon={<AlertCircle size={20} />} sx={{ mb: 3 }}>
					No grades are assigned to your account yet. Ask your administrator
					to assign classes, then return here to manage your timetable.
				</Alert>
			)}

			<Grid container spacing={3}>
				{/* Weekly timetable grid */}
				<Grid item xs={12} md={7}>
					<Card>
						<CardContent>
							<Stack
								direction="row"
								justifyContent="space-between"
								alignItems="center"
								sx={{ mb: 2 }}>
								<Typography variant="h6">
									Weekly Timetable
									{weeklyGradeName && (
										<Typography
											component="span"
											variant="subtitle1"
											color="text.secondary"
											sx={{ ml: 1 }}>
											({weeklyGradeName})
										</Typography>
									)}
								</Typography>
								<Button
									startIcon={<Plus size={20} />}
									variant="contained"
									onClick={() => {
										setFormGradeId(
											weeklyGradeId || teacherGradeOptions[0]?.id || ""
										);
										setAddDialog(true);
									}}
									disabled={teacherGradeOptions.length === 0}
									sx={{
										backgroundColor: "#6366f1",
										"&:hover": { backgroundColor: "#4f46e5" },
									}}>
									Add to Timetable
								</Button>
							</Stack>

							<FormControl fullWidth size="small" sx={{ mb: 2 }}>
								<InputLabel>Grade</InputLabel>
								<Select
									label="Grade"
									value={weeklyGradeId || ""}
									onChange={(e) => setWeeklyGradeId(e.target.value)}
									disabled={teacherGradeOptions.length === 0}>
									{teacherGradeOptions.map((g) => (
										<MenuItem key={g.id} value={g.id}>
											{g.name}
										</MenuItem>
									))}
								</Select>
							</FormControl>

							<TableContainer component={Paper} variant="outlined">
								<Table>
									<TableHead>
										<TableRow>
											<TableCell>Day</TableCell>
											<TableCell>Period</TableCell>
											<TableCell>Timetable Type</TableCell>
											<TableCell>Status</TableCell>
											<TableCell align="right">Actions</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{loading ? (
											<TableRow>
												<TableCell colSpan={5} align="center" sx={{ p: 3 }}>
													<CircularProgress size={24} />
												</TableCell>
											</TableRow>
										) : schedulesForTable.length > 0 ? (
											schedulesForTable.map((schedule) => (
												<TableRow
													key={schedule.id}
													sx={{
														backgroundColor:
															schedule.status === "cancelled"
																? "#fee2e2"
																: "inherit",
													}}>
													<TableCell>
														<Stack>
															<Typography variant="body2">
																{getDayLabel(schedule.day_of_week)}
															</Typography>
															{schedule.schedule_type === "recurring" &&
																schedule.recurring_pattern && (
																	<Typography
																		variant="caption"
																		color="text.secondary">
																		Repeats{" "}
																		{schedule.recurring_pattern.frequency},
																		every{" "}
																		{schedule.recurring_pattern.repeatEvery}{" "}
																		{schedule.recurring_pattern.frequency ===
																		"weekly"
																			? "week(s)"
																			: "month(s)"}
																	</Typography>
																)}
														</Stack>
													</TableCell>
													<TableCell>Period {schedule.period}</TableCell>
													<TableCell>
														<Stack
															direction="row"
															spacing={1}
															alignItems="center">
															{schedule.schedule_type === "recurring" && (
																<Repeat size={16} />
															)}
															<Typography
																variant="body2"
																sx={{ textTransform: "capitalize" }}>
																{schedule.schedule_type || "single"}
															</Typography>
														</Stack>
													</TableCell>
													<TableCell>
														<Typography
															sx={{
																textTransform: "capitalize",
																color:
																	schedule.status === "cancelled"
																		? "#dc2626"
																		: "#059669",
															}}>
															{schedule.status}
														</Typography>
													</TableCell>
													<TableCell align="right">
														<Stack
															direction="row"
															spacing={1}
															justifyContent="flex-end">
															{schedule.status === "active" && (
																<>
																	<IconButton
																		size="small"
																		onClick={() =>
																			handleCancelSchedule(schedule.id)
																		}
																		sx={{ color: "#dc2626" }}>
																		<X size={16} />
																	</IconButton>
																	<IconButton
																		size="small"
																		onClick={() => openEditDialog(schedule)}
																		sx={{ color: "#2563eb" }}>
																		<Edit2 size={16} />
																	</IconButton>
																</>
															)}
														</Stack>
													</TableCell>
												</TableRow>
											))
										) : (
											<TableRow>
												<TableCell colSpan={5} align="center">
													<Typography color="text.secondary">
														{weeklyGradeId
															? "No timetable entries found for this grade"
															: "Select a grade to view your timetable"}
													</Typography>
												</TableCell>
											</TableRow>
										)}
									</TableBody>
								</Table>
							</TableContainer>
						</CardContent>
					</Card>
				</Grid>

				{/* Timetable overview */}
				<Grid item xs={12} md={5}>
					<Card>
						<CardContent>
							<Typography variant="h6" sx={{ mb: 2 }}>
								Timetable Overview
								{overviewGradeName && (
									<Typography
										component="span"
										variant="subtitle1"
										color="text.secondary"
										sx={{ ml: 1 }}>
										({overviewGradeName})
									</Typography>
								)}
							</Typography>

							<FormControl fullWidth size="small" sx={{ mb: 2 }}>
								<InputLabel>Grade</InputLabel>
								<Select
									label="Grade"
									value={overviewGradeId || ""}
									onChange={(e) => setOverviewGradeId(e.target.value)}
									disabled={teacherGradeOptions.length === 0}>
									{teacherGradeOptions.map((g) => (
										<MenuItem key={g.id} value={g.id}>
											{g.name}
										</MenuItem>
									))}
								</Select>
							</FormControl>

							{loading ? (
								<Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
									<CircularProgress size={24} />
								</Box>
							) : (
								<TableContainer component={Paper} variant="outlined">
									<Table size="small">
										<TableHead>
											<TableRow>
												<TableCell
													rowSpan={2}
													sx={{
														borderRight: "1px solid rgba(224, 224, 224, 1)",
														fontWeight: "bold",
														verticalAlign: "middle",
													}}>
													Day
												</TableCell>
												<TableCell
													align="center"
													colSpan={PERIODS.length}
													sx={{
														borderBottom: "1px solid rgba(224, 224, 224, 1)",
														fontWeight: "bold",
													}}>
													Periods
												</TableCell>
											</TableRow>
											<TableRow>
												{PERIODS.map((period) => (
													<TableCell
														key={period.value}
														align="center"
														sx={{
															px: 1,
															fontWeight: "bold",
														}}>
														{period.value}
													</TableCell>
												))}
											</TableRow>
										</TableHead>
										<TableBody>
											{DAYS_OF_WEEK.map((day) => (
												<TableRow key={day.value}>
													<TableCell
														sx={{
															borderRight: "1px solid rgba(224, 224, 224, 1)",
															fontWeight: "bold",
														}}>
														{day.label}
													</TableCell>
													{PERIODS.map((period) => {
														const hasSchedule = schedulesForOverview.some(
															(s) =>
																s.day_of_week === day.value &&
																s.period === period.value &&
																s.status === "active"
														);
														return (
															<TableCell
																key={period.value}
																align="center"
																sx={{
																	backgroundColor: hasSchedule
																		? "#dcfce7"
																		: "inherit",
																	color: hasSchedule ? "#059669" : "inherit",
																	px: 1,
																}}>
																{hasSchedule ? "✓" : "-"}
															</TableCell>
														);
													})}
												</TableRow>
											))}
										</TableBody>
									</Table>
								</TableContainer>
							)}
						</CardContent>
					</Card>
				</Grid>
			</Grid>

			{/* Add timetable entry dialog */}
			<Dialog
				open={addDialog}
				onClose={() => {
					if (!isSubmitting) {
						setAddDialog(false);
						resetForm();
					}
				}}
				maxWidth="xs"
				fullWidth>
				<DialogTitle>Add Timetable Entry</DialogTitle>
				<DialogContent sx={{ pt: 2 }}>
					<ScheduleFormContent />
				</DialogContent>
				<DialogActions sx={{ p: 2, pt: 1 }}>
					<Button
						onClick={() => {
							setAddDialog(false);
							resetForm();
						}}
						variant="outlined"
						size="small"
						disabled={isSubmitting}
						sx={{
							color: "#6b7280",
							borderColor: "#e5e7eb",
							"&:hover": {
								borderColor: "#d1d5db",
								backgroundColor: "#f9fafb",
							},
						}}>
						Cancel
					</Button>
					<Button
						onClick={handleAddSchedule}
						variant="contained"
						size="small"
						disabled={
							!selectedDay ||
							!selectedPeriod ||
							!startDate ||
							isSubmitting ||
							!formGradeId
						}
						sx={{
							backgroundColor: "#6366f1",
							"&:hover": { backgroundColor: "#4f46e5" },
						}}
						startIcon={
							isSubmitting ? (
								<CircularProgress size={16} color="inherit" />
							) : null
						}>
						{isSubmitting ? "Adding..." : "Add to Timetable"}
					</Button>
				</DialogActions>
			</Dialog>

			{/* Edit timetable entry dialog */}
			<Dialog
				open={editDialog}
				onClose={() => {
					if (!isSubmitting) {
						setEditDialog(false);
						resetForm();
					}
				}}
				maxWidth="xs"
				fullWidth>
				<DialogTitle>Edit Timetable Entry</DialogTitle>
				<DialogContent sx={{ pt: 2 }}>
					<ScheduleFormContent />
				</DialogContent>
				<DialogActions sx={{ p: 2, pt: 1 }}>
					<Button
						onClick={() => {
							setEditDialog(false);
							resetForm();
						}}
						variant="outlined"
						size="small"
						disabled={isSubmitting}
						sx={{
							color: "#6b7280",
							borderColor: "#e5e7eb",
							"&:hover": {
								borderColor: "#d1d5db",
								backgroundColor: "#f9fafb",
							},
						}}>
						Cancel
					</Button>
					<Button
						onClick={handleUpdateSchedule}
						variant="contained"
						size="small"
						disabled={
							!selectedDay ||
							!selectedPeriod ||
							!startDate ||
							isSubmitting ||
							!formGradeId
						}
						sx={{
							backgroundColor: "#6366f1",
							"&:hover": { backgroundColor: "#4f46e5" },
						}}
						startIcon={
							isSubmitting ? (
								<CircularProgress size={16} color="inherit" />
							) : null
						}>
						{isSubmitting ? "Updating..." : "Update Timetable"}
					</Button>
				</DialogActions>
			</Dialog>

			{/* Alert Snackbar */}
			<Snackbar
				open={alert.open}
				autoHideDuration={6000}
				onClose={() => setAlert((prev) => ({ ...prev, open: false }))}
				anchorOrigin={{ vertical: "top", horizontal: "right" }}>
				<Alert
					onClose={() => setAlert((prev) => ({ ...prev, open: false }))}
					severity={alert.severity}
					sx={{ width: "100%" }}>
					{alert.message}
				</Alert>
			</Snackbar>
		</Box>
	);
};

export default CourseSchedule;
